import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    Dialog,
    DialogPanel,
    DialogBackdrop,
    ComboboxOption,
} from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import {
    CodeBracketSquareIcon as CodeBracketSquareIconOutline,
    DocumentTextIcon as DocumentTextIconOutline,
    TableCellsIcon as TableCellsIconOutline,
    UsersIcon as UsersIconOutline,
} from '@heroicons/react/24/outline';
import {
    CodeBracketSquareIcon as CodeBracketSquareIconSolid,
    DocumentTextIcon as DocumentTextIconSolid,
    TableCellsIcon as TableCellsIconSolid,
    UsersIcon as UsersIconSolid,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { ISlimApp } from '@biolibtech/biolib-js';
import { useGetAppsQuery } from '../../apiHooks/appsApi';
import { useListExperimentsQuery } from '../../apiHooks/experimentApi';
import { useIsSignedIn, useGetUserGroupsQuery } from '../../apiHooks/userApi';
import { teamSubdomainSafeNavigate } from '../../utils/navigation';
import SearchModalResourcesTab, { SearchTab, SearchSelection } from './SearchModalApplicationsTab';
import SearchModalResultsTab from './SearchModalResultsTab';
import SearchModalGroupsTab from './SearchModalGroupsTab';
import { filterPrefetchedApps } from './searchUtils';

interface ISearchTab {
    name: SearchTab;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconSolid: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

function getVisibleTabs(isSignedIn: boolean): ISearchTab[] {
    const tabs: ISearchTab[] = [
        { name: SearchTab.Applications, icon: CodeBracketSquareIconOutline, iconSolid: CodeBracketSquareIconSolid },
    ];
    if (isSignedIn) {
        tabs.push(
            { name: SearchTab.Results, icon: TableCellsIconOutline, iconSolid: TableCellsIconSolid },
            { name: SearchTab.DataRecords, icon: DocumentTextIconOutline, iconSolid: DocumentTextIconSolid },
            { name: SearchTab.Groups, icon: UsersIconOutline, iconSolid: UsersIconSolid },
        );
    }
    return tabs;
}

interface IProps {
    open: boolean;
    onClose: () => void;
    prefetchedApps: ISlimApp[];
}

export default function SearchModal(props: IProps) {
    const { open, onClose, prefetchedApps } = props;
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isDebouncing, setIsDebouncing] = useState(false);
    const [selectedTab, setSelectedTab] = useState<SearchTab>(SearchTab.Applications);
    const [isAltPressed, setIsAltPressed] = useState(false);
    const isSignedIn = useIsSignedIn();
    const inputRef = useRef<HTMLInputElement>(null);

    const [visibleTabs, setVisibleTabs] = useState<ISearchTab[]>(() => getVisibleTabs(isSignedIn));

    useEffect(() => {
        setVisibleTabs(getVisibleTabs(isSignedIn));
    }, [isSignedIn]);

    useEffect(() => {
        if (query === '') {
            setDebouncedQuery('');
            setIsDebouncing(false);
            return;
        }

        setIsDebouncing(true);
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
            setIsDebouncing(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [query]);

    const showApps = selectedTab === SearchTab.Applications;
    const showDataRecords = selectedTab === SearchTab.DataRecords;
    const showResults = selectedTab === SearchTab.Results;
    const showGroups = selectedTab === SearchTab.Groups;

    const prefetchedMatches = useMemo(() => {
        if (!showApps) {
            return [];
        }
        return filterPrefetchedApps(prefetchedApps, query);
    }, [query, prefetchedApps, showApps]);

    const { data: appsData, isFetching: isFetchingApps } = useGetAppsQuery(
        {
            page: 1,
            page_size: 5,
            search: debouncedQuery || undefined,
            resource_type: 'app',
        },
        { skip: !debouncedQuery || !showApps },
    );

    const { data: dataRecordsData, isFetching: isFetchingDataRecords } = useGetAppsQuery(
        {
            page: 1,
            page_size: 5,
            search: debouncedQuery || undefined,
            resource_type: 'data-record',
        },
        { skip: !debouncedQuery || !showDataRecords },
    );

    const { data: resultsData, isFetching: isFetchingResults } = useListExperimentsQuery(
        {
            page: 1,
            page_size: 5,
            search: debouncedQuery || undefined,
        },
        { skip: !debouncedQuery || !isSignedIn || !showResults },
    );

    const { data: groupsData, isFetching: isFetchingGroups } = useGetUserGroupsQuery(
        { pageSize: 1000 },
        { skip: !query || !isSignedIn || !showGroups },
    );

    const apps = appsData?.results || [];
    const dataRecords = dataRecordsData?.results || [];
    const results = resultsData?.results || [];
    const groups = groupsData?.results || [];

    const isAppsLoading = showApps && (isDebouncing || isFetchingApps);
    const isDataRecordsLoading = showDataRecords && (isDebouncing || isFetchingDataRecords);
    const isResultsLoading = showResults && (isDebouncing || isFetchingResults);
    const isGroupsLoading = showGroups && isFetchingGroups;

    useEffect(() => {
        if (!open) {
            setQuery('');
            setDebouncedQuery('');
            setSelectedTab(SearchTab.Applications);
            setIsAltPressed(false);
        }
    }, [open]);

    return (
        <Dialog className='relative z-50' open={open} onClose={onClose} initialFocus={inputRef}>
            <DialogBackdrop
                transition={true}
                className='fixed inset-0 bg-gray-500/25 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in dark:bg-gray-900/50'
            />

            <div
                className='fixed inset-0 z-10 w-screen overflow-y-auto p-4 px-6 py-2'
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                        onClose();
                    }
                }}
            >
                <DialogPanel
                    transition={true}
                    className='mx-auto max-w-2xl xl:max-w-3xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl outline outline-1 outline-black/5 transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in focus:outline-none focus-visible:outline-none dark:divide-white/10 dark:bg-gray-900 dark:-outline-offset-1 dark:outline-white/10'
                >
                    <Combobox
                        onChange={(item: SearchSelection | null) => {
                            if (item && 'path' in item) {
                                teamSubdomainSafeNavigate(item.path);
                                onClose();
                            }
                        }}
                    >
                        <div className='grid grid-cols-1'>
                            <ComboboxInput
                                ref={inputRef}
                                autoComplete='off'
                                className='col-start-1 row-start-1 h-12 w-full pl-11 pr-16 text-base text-gray-900 border-0 focus:border-0 appearance-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0 focus:border-transparent placeholder:text-gray-400 placeholder:font-medium sm:text-sm dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500'
                                placeholder='Search BioLib'
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={(event) => {
                                    setIsAltPressed(event.altKey);
                                    if (event.key === 'Tab') {
                                        event.preventDefault();
                                        const arrowKey = event.shiftKey ? 'ArrowUp' : 'ArrowDown';
                                        const keyCode = event.shiftKey ? 38 : 40;
                                        const arrowEvent = new KeyboardEvent('keydown', {
                                            key: arrowKey,
                                            code: arrowKey,
                                            keyCode: keyCode,
                                            which: keyCode,
                                            bubbles: true,
                                            cancelable: true,
                                        });
                                        inputRef.current?.dispatchEvent(arrowEvent);
                                    }
                                    const digitCodeMatch = event.altKey && event.code.match(/^Digit([1-9])$/);
                                    if (digitCodeMatch) {
                                        event.preventDefault();
                                        const tabIndex = parseInt(digitCodeMatch[1], 10) - 1;
                                        if (tabIndex < visibleTabs.length) {
                                            setSelectedTab(visibleTabs[tabIndex].name);
                                        }
                                    }
                                }}
                                onKeyUp={(event) => setIsAltPressed(event.altKey)}
                                onBlur={() => setIsAltPressed(false)}
                            />
                            <MagnifyingGlassIcon
                                className='pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-400 dark:text-gray-500'
                                aria-hidden='true'
                            />
                            <button
                                type='button'
                                onClick={onClose}
                                className='col-start-1 row-start-1 mr-4 self-center justify-self-end rounded border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-400 cursor-pointer dark:border-gray-600 dark:text-gray-500'
                                title='Press Escape key to close'
                            >
                                esc
                            </button>
                        </div>
                        <ComboboxOptions static as='div'>
                            <nav className='flex h-12 px-3 items-center border-b border-gray-100 dark:border-white/10'>
                                {visibleTabs.map((tab, index) => {
                                    const isSelected = tab.name === selectedTab;
                                    const Icon = isSelected ? tab.iconSolid : tab.icon;
                                    return (
                                        <ComboboxOption value={null} key={tab.name} disabled={true}>
                                            <div
                                                onClick={() => setSelectedTab(tab.name)}
                                                className={clsx(
                                                    'relative whitespace-nowrap border-b-2 p-3 text-sm inline-flex items-center cursor-pointer',
                                                    isSelected
                                                        ? 'border-cyan-800 text-cyan-800 dark:border-cyan-400 dark:text-cyan-400'
                                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300',
                                                )}
                                            >
                                                <kbd
                                                    className={clsx(
                                                        'pointer-events-none absolute left-3 top-1/2 inline-flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded border border-gray-300 bg-gray-100 text-[9px] font-medium leading-none text-gray-500 transition-opacity dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400',
                                                        isAltPressed ? 'opacity-100' : 'opacity-0',
                                                    )}
                                                >
                                                    {index + 1}
                                                </kbd>
                                                <Icon className='h-4 w-4 mr-1' aria-hidden='true' />
                                                <span className='inline-grid'>
                                                    <span
                                                        className={clsx(
                                                            'col-start-1 row-start-1',
                                                            isSelected ? 'font-bold' : 'font-normal',
                                                        )}
                                                    >
                                                        {tab.name}
                                                    </span>
                                                    <span
                                                        aria-hidden='true'
                                                        className='font-bold invisible col-start-1 row-start-1'
                                                    >
                                                        {tab.name}
                                                    </span>
                                                </span>
                                            </div>
                                        </ComboboxOption>
                                    );
                                })}
                            </nav>

                            <ul className='h-[24rem] scroll-py-2 overflow-y-auto'>
                                {query === '' && (
                                    <li className='p-2'>
                                        <p className='px-3 py-32 text-center text-gray-500 dark:text-gray-400'>
                                            Start typing to search {selectedTab.toLowerCase()}
                                        </p>
                                    </li>
                                )}

                                {query !== '' && (
                                    <>
                                        {showApps && (
                                            <SearchModalResourcesTab
                                                isLoading={isAppsLoading}
                                                query={query}
                                                resources={apps}
                                                resourceTypeLabel='applications'
                                                totalCount={appsData?.object_count}
                                                prefetchedMatches={prefetchedMatches}
                                            />
                                        )}

                                        {showResults && (
                                            <SearchModalResultsTab
                                                isLoading={isResultsLoading}
                                                query={query}
                                                results={results}
                                                totalCount={resultsData?.object_count}
                                            />
                                        )}

                                        {showDataRecords && (
                                            <SearchModalResourcesTab
                                                isLoading={isDataRecordsLoading}
                                                query={query}
                                                resources={dataRecords}
                                                resourceTypeLabel='data records'
                                                totalCount={dataRecordsData?.object_count}
                                            />
                                        )}

                                        {showGroups && (
                                            <SearchModalGroupsTab
                                                isLoading={isGroupsLoading}
                                                query={query}
                                                groups={groups}
                                            />
                                        )}
                                    </>
                                )}
                            </ul>
                        </ComboboxOptions>
                    </Combobox>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
